import React, { useState, useEffect } from 'react';
import { Table, Card, Statistic, Row, Col, Tag, message, Spin, Input, Space, Tooltip, Button, Popconfirm } from 'antd';
import { UserOutlined, CheckCircleOutlined, CloseCircleOutlined, SearchOutlined, DeleteOutlined } from '@ant-design/icons';
import api from '../../services/api';

const { Search } = Input;

const StudentBindingManagement = ({ classId }) => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [students, setStudents] = useState([]);
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [searchKeyword, setSearchKeyword] = useState('');

  useEffect(() => {
    if (classId) {
      fetchBindingData();
    }
  }, [classId]);

  useEffect(() => {
    filterStudents();
  }, [searchKeyword, students]);

  const fetchBindingData = async () => {
    try {
      setLoading(true);
      
      // 获取统计信息
      const statsResponse = await api.get(`/parent/bindng/class-bindng-stats/${classId}`);
      if (statsResponse.data.success) {
        setStats(statsResponse.data.data);
      }
      
      // 获取详细绑定信息
      const studentsResponse = await api.get(`/parent/bindng/class-students-binding/${classId}`);
      if (studentsResponse.data.success) {
        setStudents(studentsResponse.data.data);
        setFilteredStudents(studentsResponse.data.data);
      }
    } catch (error) {
      console.error('获取绑定信息失败:', error);
      if (error.response?.status === 403) {
        message.error('您无权查看该班级的绑定信息');
      } else {
        message.error('获取绑定信息失败');
      }
    } finally {
      setLoading(false);
    }
  };

  const filterStudents = () => {
    if (!searchKeyword) {
      setFilteredStudents(students);
      return;
    }

    const keyword = searchKeyword.toLowerCase();
    const filtered = students.filter(student => 
      student.student_name.toLowerCase().includes(keyword) ||
      (student.seat_number && student.seat_number.toString().includes(keyword)) ||
      (student.parent1_name && student.parent1_name.toLowerCase().includes(keyword)) ||
      (student.parent2_name && student.parent2_name.toLowerCase().includes(keyword))
    );
    setFilteredStudents(filtered);
  };

  const handleUnbind = async (studentId, parentUserId, studentName, parentName) => {
    try {
      await api.delete(`/parent/bindng/teacher-unbind/${studentId}/${parentUserId}`);
      message.success(`已解绑 ${studentName} 与 ${parentName} 的绑定关系`);
      // 重新获取数据
      fetchBindingData();
    } catch (error) {
      console.error('解绑失败:', error);
      if (error.response?.status === 403) {
        message.error('您无权执行此操作');
      } else if (error.response?.status === 404) {
        message.error('绑定关系不存在或已解绑');
      } else {
        message.error(error.response?.data?.message || '解绑失败');
      }
    }
  };

  const columns = [
    {
      title: '座位号',
      dataIndex: 'seat_number',
      key: 'seat_number',
      width: 80,
      sorter: (a, b) => {
        const aNum = parseInt(a.seat_number) || 9999;
        const bNum = parseInt(b.seat_number) || 9999;
        return aNum - bNum;
      },
    },
    {
      title: '学生姓名',
      dataIndex: 'student_name',
      key: 'student_name',
      width: 120,
    },
    {
      title: '家长信息',
      key: 'parent_info',
      width: 200,
      render: (_, record) => (
        <div>
          {record.parent1_name && (
            <div style={{ marginBottom: 4 }}>
              <span style={{ color: '#666' }}>家长1：</span>
              <span>{record.parent1_name}</span>
              {record.parent1_phone && (
                <span style={{ color: '#999', fontSize: 12, marginLeft: 8 }}>
                  {record.parent1_phone.replace(/(\d{3})\d{4}(\d{4})/, '$1****$2')}
                </span>
              )}
            </div>
          )}
          {record.parent2_name && (
            <div>
              <span style={{ color: '#666' }}>家长2：</span>
              <span>{record.parent2_name}</span>
              {record.parent2_phone && (
                <span style={{ color: '#999', fontSize: 12, marginLeft: 8 }}>
                  {record.parent2_phone.replace(/(\d{3})\d{4}(\d{4})/, '$1****$2')}
                </span>
              )}
            </div>
          )}
          {!record.parent1_name && !record.parent2_name && (
            <span style={{ color: '#999' }}>未填写</span>
          )}
        </div>
      ),
    },
    {
      title: '绑定状态',
      key: 'binding_status',
      width: 120,
      render: (_, record) => {
        if (record.is_bound) {
          return (
            <Tooltip title={`已绑定 ${record.bound_parent_count} 位家长`}>
              <Tag color="success" icon={<CheckCircleOutlined />}>
                已绑定 ({record.bound_parent_count})
              </Tag>
            </Tooltip>
          );
        } else {
          return (
            <Tag color="default" icon={<CloseCircleOutlined />}>
              未绑定
            </Tag>
          );
        }
      },
    },
    {
      title: '已绑定家长',
      key: 'bound_parents',
      width: 300,
      render: (_, record) => {
        if (record.bound_parents && record.bound_parents.length > 0) {
          return (
            <div>
              {record.bound_parents.map((parent, index) => (
                <div key={index} style={{ marginBottom: 8, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ flex: 1 }}>
                    <UserOutlined style={{ marginRight: 4, color: '#1890ff' }} />
                    <span>{parent.name}</span>
                    {parent.nickname && parent.nickname !== parent.name && (
                      <span style={{ color: '#999', fontSize: 12, marginLeft: 4 }}>
                        ({parent.nickname})
                      </span>
                    )}
                    {parent.bindTime && (
                      <span style={{ color: '#999', fontSize: 11, marginLeft: 8 }}>
                        {parent.bindTime.split(' ')[0]}
                      </span>
                    )}
                  </div>
                  <Popconfirm
                    title={`确定要解绑 ${record.student_name} 与 ${parent.name} 的绑定关系吗？`}
                    description="解绑后，该家长将无法再查看该学生的信息"
                    onConfirm={() => handleUnbind(record.student_id, parent.parent_user_id, record.student_name, parent.name)}
                    okText="确定"
                    cancelText="取消"
                    okButtonProps={{ danger: true }}
                  >
                    <Button
                      type="link"
                      danger
                      size="small"
                      icon={<DeleteOutlined />}
                      style={{ padding: 0, marginLeft: 8 }}
                    >
                      解绑
                    </Button>
                  </Popconfirm>
                </div>
              ))}
            </div>
          );
        } else {
          return <span style={{ color: '#999' }}>暂无</span>;
        }
      },
    },
  ];

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ padding: '20px 0' }}>
      {/* 统计卡片 */}
      {stats && (
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col span={6}>
            <Card>
              <Statistic
                title="班级学生总数"
                value={stats.totalStudents}
                prefix={<UserOutlined />}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="已绑定学生数"
                value={stats.bindngCount}
                valueStyle={{ color: '#3f8600' }}
                prefix={<CheckCircleOutlined />}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="未绑定学生数"
                value={stats.unbindngCount}
                valueStyle={{ color: '#cf1322' }}
                prefix={<CloseCircleOutlined />}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="绑定率"
                value={stats.bindngRate}
                suffix="%"
                valueStyle={{ color: '#1890ff' }}
              />
            </Card>
          </Col>
        </Row>
      )}

      {/* 搜索框 */}
      <Card style={{ marginBottom: 16 }}>
        <Space style={{ width: '100%', justifyContent: 'space-between' }}>
          <Search
            placeholder="搜索学生姓名、座位号、家长姓名"
            allowClear
            enterButton={<SearchOutlined />}
            value={searchKeyword}
            onChange={(e) => setSearchKeyword(e.target.value)}
            onSearch={filterStudents}
            style={{ width: 400 }}
          />
          <div style={{ color: '#666' }}>
            共 {filteredStudents.length} 名学生
          </div>
        </Space>
      </Card>

      {/* 学生列表 */}
      <Card>
        <Table
          columns={columns}
          dataSource={filteredStudents}
          rowKey="student_id"
          pagination={{
            pageSize: 20,
            showSizeChanger: true,
            showTotal: (total) => `共 ${total} 条记录`,
          }}
          scroll={{ x: 800 }}
        />
      </Card>
    </div>
  );
};

export default StudentBindingManagement;

